import React, { useContext, useEffect, useState } from 'react'
import API from '../../../services/API'
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Grid,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'
import DetailsBox, { DetailRow } from '../../../components/Cards/DetailsBox'
import SmallModal from '../../../components/Modals/SmallModal'
import AddNewPolicy from './AddNewPolicy'
import BigModal from '../../../components/Modals/BigModal'
import MarkGapReviewedDialog from '../../cqcCentre/Gaps/MarkGapReviewedDialog'
import moment from 'moment'
import { useNavigate, useParams } from 'react-router-dom'
import { UserContext } from '../../../App'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'
import { useDocumentTitle } from '../../../helpers/DocumentTitleManager'

const PolicyPage = () => {
  const [policy, setPolicy] = useState({})
  const [addNew, setAddNew] = useState(false)
  const [docURL, setDocURL] = useState()
  const { policyId } = useParams()
  const { user } = useContext(UserContext)
  const allowedTeams = ['Governance']
  const isGovernanceUser = user.employee?.teams.some((team) =>
    allowedTeams.includes(team),
  ) //TODO

  const [checkConfirmation, setCheckConfirmation] = useState(false)
  const [notUploaded, setNotUploaded] = useState(false)
  const [reviewOpen, setReviewOpen] = useState(false)
  const navigate = useNavigate()
  const {setPageTitle} = useDocumentTitle()
  // Staff must open the document before they can mark it as read.
  const [hasOpened, setHasOpened] = useState(false)
  const isLargeScreen = useMediaQuery((theme) => theme.breakpoints.up('lg'))
  // Older uploads could be Word files (the picker's PDF filter was only a
  // hint); those can't be embedded, so they're offered as a download instead.
  const isPdf = /\.pdf$/i.test(policy?.docLink || '')

  const isReviewOverdue = policy?.reviewDate && moment(policy.reviewDate).isBefore(moment())

  const getAcknowledgement = (userId) => {
    return policy?.acknowledgedBy?.find(
      (ack) => ack?.user?.toString() === userId?.toString(),
    )
  }

  const hasAcknowledged = (userId) => Boolean(getAcknowledgement(userId))

  const formatAcknowledgedAt = (userId) => {
    const ack = getAcknowledgement(userId)

    if (!ack) return 'Not acknowledged'
    if (!ack.acknowledgedAt) return 'Acknowledged before date tracking'

    return moment(ack.acknowledgedAt).format('DD/MM/YYYY HH:mm')
  }

  const loadPolicy = () => {
    setCheckConfirmation(false)
    API.get('/policy/' + policyId)
      .then((res) => {
        if (res.data.notUploadedYet) {
          setNotUploaded(true)
        }
        setPolicy(res.data)
      })
      .catch((e) => console.error(e))
  }

  useEffect(() => {
    loadPolicy()
  }, [])

  useEffect(() => {
    if (policy?.description?.policyTitle) {
      setPageTitle(policy.description.policyTitle)
    }
  }, [policy?.description?.policyTitle, setPageTitle])

  useEffect(() => {
    setHasOpened(false)
    if (policy?.docLink) {
      API.post('/gets3downloadurl', {
        key: policy.docLink,
        ACL: 'public-read',
        expires: 3000,
        ...(!isPdf && {
          inlineView: false,
          filename: policy.description?.policyTitle,
        }),
      })
        .then((res) => {
          setDocURL(res.data)
        })
        .catch((e) => console.error(e))
    }
  }, [policy])

  // On large screens a PDF is embedded on the page, so showing it counts as
  // opening it.
  useEffect(() => {
    if (isPdf && docURL && isLargeScreen) setHasOpened(true)
  }, [isPdf, docURL, isLargeScreen])

  const handleConfirmPolicy = () => {
    API.get(`/policy/confirm/${policy._id}`)
      .then((res) => {
        if (res.status === 200) {
          navigate('/policy-library')
        }
      })
      .catch((e) => console.error(e))
  }

  const PDFButton = () => (
    <Link
      href={docURL}
      // href={`${process.env.REACT_APP_API_URL}/${
      //   window.location.hostname.split('.')[0]
      // }/policy/download/${policy._id}`}
      target="_blank"
      sx={{ textDecoration: 'none' }}
      width="100%"
      onClick={() => setHasOpened(true)}
    >
      <Button variant="contained" color="primary" fullWidth disabled={!docURL}>
        {isPdf ? 'View Policy' : 'Download Policy'}
      </Button>
    </Link>
  )

  return (
    <Stack maxHeight="100vh" minHeight="100%">
      <Typography variant="title2" display="flex">
        {policy?.description?.policyTitle}
      </Typography>
      <Typography variant="subtitle1">
        {policy?.description?.category}
      </Typography>
      {!policy && (
        <Box display="flex" flexDirection="column" gap={2}>
          <Alert severity="error">No Policy Found</Alert>
        </Box>
      )}
      {notUploaded && (
        <Box display="flex" flexDirection="column" gap={2}>
          <Alert severity="error">
            Policy Document has not been uploaded yet. Please try again later.
          </Alert>
        </Box>
      )}
      {notUploaded && isGovernanceUser && (
        <Button
          color="primary"
          variant="contained"
          fullWidth
          onClick={() => setAddNew(true)}
        >
          Add New
        </Button>
      )}
      {!notUploaded && (
        <Stack direction="row" minHeight="90%" gap={2}>
          <Box
            height="100%"
            display={{ xs: 'none', lg: isPdf ? 'flex' : 'none' }}
            flex={5}
          >
            <object
              data={docURL}
              type="application/pdf"
              width="100%"
              height="100%"
            >
              <p>
                Browser does not support PDF files. Please use the button below
                to view this document.
              </p>
              <PDFButton />
            </object>
          </Box>
          <Box padding={1} flex={2} boverflow="hidden">
            <Stack gap={2}>
              {!isPdf && (
                <Alert severity="warning">
                  This policy was uploaded as a Word document and can't be
                  previewed. Download it to read it.
                  {isGovernanceUser &&
                    ' Upload a PDF as a new version so staff can view it in the app.'}
                </Alert>
              )}
              <Box display={{ xs: 'flex', lg: isPdf ? 'none' : 'flex' }}>
                <PDFButton />
              </Box>
              <Box component={Paper} padding={1}>
                <Stack gap={1}>
                  <DetailRow label="Version" value={policy?.version} />
                  <DetailRow
                    label="Date"
                    value={
                      policy?.date
                        ? moment(policy.date).format('DD.MMM.YYYY')
                        : ''
                    }
                  />
                  <DetailRow label="Summary" value={policy?.versionChanges} />
                  <DetailRow
                    label="Next Review"
                    value={
                      policy?.reviewDate
                        ? `${moment(policy.reviewDate).format('DD.MMM.YYYY')}${isReviewOverdue ? ' (overdue)' : ''}`
                        : 'Not set'
                    }
                  />
                  {isGovernanceUser && (
                    <Button
                      color={isReviewOverdue ? 'warning' : 'inherit'}
                      variant="outlined"
                      fullWidth
                      onClick={() => setReviewOpen(true)}
                    >
                      Mark Reviewed
                    </Button>
                  )}
                  <Button
                    color={
                      policy?.acknowledgedByCurrentUser ? 'success' : 'error'
                    }
                    disabled={policy?.acknowledgedByCurrentUser || !hasOpened}
                    variant="contained"
                    fullWidth
                    onClick={() => setCheckConfirmation(true)}
                    startIcon={
                      policy?.acknowledgedByCurrentUser ? (
                        <CheckBoxIcon />
                      ) : (
                        <PriorityHighIcon />
                      )
                    }
                  >
                    {policy?.acknowledgedByCurrentUser
                      ? `Acknowledged ${moment(
                          policy.currentUserAcknowledgedAt,
                        ).format('DD/MM/YYYY HH:mm')}`
                      : 'Mark as Read'}
                  </Button>
                  {!policy?.acknowledgedByCurrentUser && !hasOpened && (
                    <Typography variant="caption" color="text.secondary">
                      Open the policy document before marking it as read.
                    </Typography>
                  )}
                  {isGovernanceUser && (
                    <Button
                      color="primary"
                      variant="contained"
                      fullWidth
                      onClick={() => setAddNew(true)}
                    >
                      Add New
                    </Button>
                  )}
                </Stack>
              </Box>
              {isGovernanceUser && (
                <Box
                  component={Paper}
                  elevation={0}
                  sx={{
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      background:
                        'linear-gradient(135deg, #0B1F3A 0%, #146C94 100%)',
                      color: 'white',
                    }}
                  >
                    <Typography variant="h6" fontWeight={700} color="white">
                      Staff Acknowledgements
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ opacity: 0.65, color: 'white' }}
                    >
                      {policy?.activeStaff?.filter((staff) =>
                        hasAcknowledged(staff.userAccount?._id),
                      ).length || 0}{' '}
                      of {policy?.activeStaff?.length || 0} active staff
                      acknowledged
                    </Typography>
                  </Box>

                  <Stack sx={{ maxHeight: '24rem', overflowY: 'auto' }}>
                    {policy?.activeStaff
                      ?.sort((a, b) => {
                        const aAck = hasAcknowledged(a?.userAccount?._id)
                        const bAck = hasAcknowledged(b?.userAccount?._id)

                        if (aAck !== bAck) return aAck ? -1 : 1

                        const aName = `${a.userAccount?.firstName ?? ''} ${
                          a.userAccount?.lastName ?? ''
                        }`.toLowerCase()

                        const bName = `${b.userAccount?.firstName ?? ''} ${
                          b.userAccount?.lastName ?? ''
                        }`.toLowerCase()

                        return aName.localeCompare(bName)
                      })
                      .map((staff) => {
                        const acknowledged = hasAcknowledged(
                          staff.userAccount?._id,
                        )

                        return (
                          <Box
                            key={staff._id}
                            onClick={() =>
                              navigate(`/employees/${staff.userAccount._id}`)
                            }
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 2,
                              p: 1.5,
                              cursor: 'pointer',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              '&:hover': {
                                bgcolor: 'action.hover',
                              },
                            }}
                          >
                            <Box>
                              <Typography fontWeight={600}>
                                {staff.userAccount?.firstName}{' '}
                                {staff.userAccount?.lastName}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {moment(
                                  getAcknowledgement(staff.userAccount?._id)
                                    ?.acknowledgedAt,
                                ).format('DD/MM/YYYY')}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 999,
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                bgcolor: acknowledged
                                  ? 'success.light'
                                  : 'error.light',
                                color: acknowledged
                                  ? 'success.dark'
                                  : 'error.dark',
                              }}
                            >
                              {acknowledged ? 'Acknowledged' : 'Outstanding'}
                            </Box>
                          </Box>
                        )
                      })}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Box>
        </Stack>
      )}
      <SmallModal
        open={checkConfirmation}
        onClose={() => setCheckConfirmation(false)}
      >
        <Box display="flex" flexDirection="column" gap={1}>
          <Box>Are you sure you want to mark this item as read?</Box>
          <ButtonGroup fullWidth>
            <Button color="error" onClick={() => setCheckConfirmation(false)}>
              No
            </Button>
            <Button color="success" onClick={handleConfirmPolicy}>
              Yes
            </Button>
          </ButtonGroup>
        </Box>
      </SmallModal>
      <BigModal open={addNew} onClose={() => setAddNew(false)}>
        <AddNewPolicy
          policyDescription={policy.description}
          currentVersion={policy?.version}
        />
      </BigModal>
      <BigModal open={reviewOpen} onClose={() => setReviewOpen(false)}>
        <MarkGapReviewedDialog
          gap={{
            sourceType: 'policy',
            relatedRecordId: policy._id,
            message: `Review "${policy?.description?.policyTitle}" and confirm it is still current and fit for purpose.`,
          }}
          onClose={() => setReviewOpen(false)}
          onSaved={() => {
            setReviewOpen(false)
            loadPolicy()
          }}
        />
      </BigModal>
    </Stack>
  )
}

export default PolicyPage
